/* eslint-disable simple-import-sort/imports */
import moment from "moment-timezone";
import { useMemo } from "react";

import {
	IntervalData,
	SeriesData,
	SeriesIntervalData,
} from "./pricing-graph.types";

export const GRAPH_TIMEZONE = "Asia/Singapore";

// Remove null values and parse date to start of day
export function parseDataToStartOfDay(series: SeriesData[]) {
	const filteredData = series
		?.filter((data) => data?.value !== null)
		?.map((data) => ({
			...data,
			// Parse date string explicitly as YYYY-MM-DD in Singapore timezone
			// Backend sends dates already formatted in Singapore time, so we parse them directly
			date: moment.tz(data?.date, "YYYY-MM-DD", GRAPH_TIMEZONE).startOf("day"),
		}));

	// Ensure first and last data points are never marked as extrapolated
	// These represent actual orders/trades and should always show markers
	if (filteredData && filteredData.length > 0) {
		if (filteredData[0].isExtrapolated) {
			filteredData[0] = { ...filteredData[0], isExtrapolated: false };
		}
		if (
			filteredData.length > 1 &&
			filteredData[filteredData.length - 1].isExtrapolated
		) {
			filteredData[filteredData.length - 1] = {
				...filteredData[filteredData.length - 1],
				isExtrapolated: false,
			};
		}
	}

	return filteredData;
}

export function useMemoData(data: SeriesIntervalData) {
	const { ask, bid, trade, askRef } = useMemo(() => {
		return {
			ask: parseDataToStartOfDay(data.ask),
			bid: parseDataToStartOfDay(data.bid),
			trade: parseDataToStartOfDay(data.trade),
			askRef: parseDataToStartOfDay(data.askRef),
		};
	}, [data]);

	return { ask, bid, trade, askRef };
}

export function calculateXDomain(
	data: IntervalData,
	isMonthly: boolean = false,
) {
	const pushDates = ({
		datesArr,
		startDates,
		endDates,
	}: {
		datesArr: { date: moment.Moment; value: number }[];
		startDates: moment.Moment[];
		endDates: moment.Moment[];
	}) => {
		if (!datesArr || datesArr.length === 0) return;
		const dates = datesArr.map((entry) => entry.date).filter(Boolean);
		if (dates.length === 0) return;
		startDates.push(moment.min(dates));
		endDates.push(moment.max(dates));
	};

	const startDates: moment.Moment[] = [],
		endDates: moment.Moment[] = [];
	pushDates({ datesArr: data.ask, startDates, endDates });
	pushDates({ datesArr: data.bid, startDates, endDates });
	pushDates({ datesArr: data.trade, startDates, endDates });
	pushDates({ datesArr: data.fundingSeries || [], startDates, endDates });
	pushDates({ datesArr: data.askRef, startDates, endDates });

	const xDomain = [
		moment.min(startDates),
		isMonthly ? moment.max(endDates) : moment.tz(GRAPH_TIMEZONE).startOf("day"),
	];
	return xDomain;
}
